
import type {
  Identifiable,
  QueryRankingElementRankingDTO,
  QueryRankingHiddenElementDTO,
  QueryRankingPinnedElementDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import { QueryRankingElementRankingAction } from '@empathyco/playboard-core-domain'
import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { CONTEXT_CONSTS } from '../../store/context/context.consts'

import { ConfigurationsUtils } from '../../utils'

import MutableList from '../mutable-list.component.vue'
import RankingElementsListItem from './ranking-elements-list-item.component.vue'

type ElementWithId = (
  | QueryRankingElementRankingDTO
  | QueryRankingHiddenElementDTO
  | QueryRankingPinnedElementDTO
) &
  Identifiable

export default defineComponent({
  name: 'RankingElementsList',
  components: {
    MutableList,
    RankingElementsListItem,
  },
  props: {
    elements: {
      type: Array as PropType<
        Array<
          | QueryRankingElementRankingDTO
          | QueryRankingPinnedElementDTO
          | QueryRankingHiddenElementDTO
        >
      >,
      required: true,
    },
    disabledFieldNames: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    readOnly: Boolean,
    messages: {
      type: Object,
      required: true,
    },
    sortable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    sortedElementsByAction(): Array<ElementWithId> {
      return this.elements
        .map(element => ({
          ...element,
          id: ConfigurationsUtils.getRankingElementId(element),
        }))
        .sort((element1, element2) => {
          if (element1.action !== element2.action) {
            return element1.action === QueryRankingElementRankingAction.BOOST ? -1 : 1
          }
          return 0
        })
    },
    disabledItemIds(): string[] {
      return this.elements
        .filter(element => this.disabledFieldNames.includes(element.fieldName))
        .map(element => ConfigurationsUtils.getRankingElementId(element))
    },
  },
  methods: {
    getIndexOfElement(element: ElementWithId): number {
      return this.elements.findIndex(
        (el: QueryRankingElementRankingDTO) =>
          ConfigurationsUtils.getRankingElementId(el) === element.id,
      )
    },
    onRemoveElement(element: ElementWithId): void {
      this.elements.splice(this.getIndexOfElement(element), 1)
    },
    onMoveElement({ item: elementToMove, targetIndex }): void {
      const { id: elementId, ...element } = elementToMove
      const targetElement = this.sortedElementsByAction[targetIndex]
      const updatedElements = [...this.elements]
      const oldIndex = this.getIndexOfElement(elementToMove)
      const newIndex = this.getIndexOfElement(targetElement)
      updatedElements.splice(oldIndex, 1)
      updatedElements.splice(newIndex, 0, element)
      this.$emit('updateElements', updatedElements)
    },
  },
})
